// @ts-nocheck
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import type {
  ErrorResponse,
  PublicStoryDetailResponse,
  PublicStoryReportResponse,
  StoryReportCreate,
  ValidationErrorResponse,
} from "../../models";
import { customInstance } from "../../custom-instance";

type SecondParameter<T extends (...args: any) => any> = Parameters<T>[1];

/**
 * Retrieve a publicly shared story by its share slug. This endpoint is accessible without authentication. IMPORTANT: Reader profile data (child's name, age) is NOT included to protect child privacy. Only story content and character names are visible.
 * @summary Get public story details
 */
export const publicStoriesDetail = (
  slug: string,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<PublicStoryDetailResponse>(
    { url: `/api/stories/public/${slug}/`, method: "GET", signal },
    options
  );
};

export const getPublicStoriesDetailQueryKey = (slug: string) => {
  return [`/api/stories/public/${slug}/`] as const;
};

export const getPublicStoriesDetailQueryOptions = <
  TData = Awaited<ReturnType<typeof publicStoriesDetail>>,
  TError = ErrorResponse
>(
  slug: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof publicStoriesDetail>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getPublicStoriesDetailQueryKey(slug);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof publicStoriesDetail>>
  > = ({ signal }) => publicStoriesDetail(slug, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!slug,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof publicStoriesDetail>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PublicStoriesDetailQueryResult = NonNullable<
  Awaited<ReturnType<typeof publicStoriesDetail>>
>;
export type PublicStoriesDetailQueryError = ErrorResponse;

export function usePublicStoriesDetail<
  TData = Awaited<ReturnType<typeof publicStoriesDetail>>,
  TError = ErrorResponse
>(
  slug: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof publicStoriesDetail>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof publicStoriesDetail>>,
          TError,
          Awaited<ReturnType<typeof publicStoriesDetail>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  }
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function usePublicStoriesDetail<
  TData = Awaited<ReturnType<typeof publicStoriesDetail>>,
  TError = ErrorResponse
>(
  slug: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof publicStoriesDetail>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof publicStoriesDetail>>,
          TError,
          Awaited<ReturnType<typeof publicStoriesDetail>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function usePublicStoriesDetail<
  TData = Awaited<ReturnType<typeof publicStoriesDetail>>,
  TError = ErrorResponse
>(
  slug: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof publicStoriesDetail>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get public story details
 */

export function usePublicStoriesDetail<
  TData = Awaited<ReturnType<typeof publicStoriesDetail>>,
  TError = ErrorResponse
>(
  slug: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof publicStoriesDetail>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getPublicStoriesDetailQueryOptions(slug, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Report a publicly shared story for inappropriate content, copyright violation, or other concerns. This endpoint is accessible without authentication. Optionally provide an email address for follow-up. Required for App Store compliance with UGC guidelines.
 * @summary Report a public story
 */
export const publicStoriesReport = (
  slug: string,
  storyReportCreate: StoryReportCreate,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<PublicStoryReportResponse>(
    {
      url: `/api/stories/public/${slug}/report/`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: storyReportCreate,
      signal,
    },
    options
  );
};

export const getPublicStoriesReportMutationOptions = <
  TError = ValidationErrorResponse | ErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof publicStoriesReport>>,
    TError,
    { slug: string; data: StoryReportCreate },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof publicStoriesReport>>,
  TError,
  { slug: string; data: StoryReportCreate },
  TContext
> => {
  const mutationKey = ["publicStoriesReport"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof publicStoriesReport>>,
    { slug: string; data: StoryReportCreate }
  > = (props) => {
    const { slug, data } = props ?? {};

    return publicStoriesReport(slug, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PublicStoriesReportMutationResult = NonNullable<
  Awaited<ReturnType<typeof publicStoriesReport>>
>;
export type PublicStoriesReportMutationBody = StoryReportCreate;
export type PublicStoriesReportMutationError =
  | ValidationErrorResponse
  | ErrorResponse;

/**
 * @summary Report a public story
 */
export const usePublicStoriesReport = <
  TError = ValidationErrorResponse | ErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof publicStoriesReport>>,
    TError,
    { slug: string; data: StoryReportCreate },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof publicStoriesReport>>,
  TError,
  { slug: string; data: StoryReportCreate },
  TContext
> => {
  const mutationOptions = getPublicStoriesReportMutationOptions(options);

  return useMutation(mutationOptions);
};
